import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Calendar as CalendarIcon, Building2, TrendingUp, ChevronRight } from 'lucide-react';
import { mockApartments } from '@mocks/daireler';

export default function WelcomeHeader() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const user = { name: 'Demo Kullanıcı', role: 'Müteahhit' };

    const today = new Date().toLocaleDateString('tr-TR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Satış istatistiklerini hesapla
    const salesStats = useMemo(() => {
        let relevantApartments = mockApartments;

        // Eğer belirli bir inşaat seçiliyse sadece ona ait daireleri al
        if (!showAllConstructions && selectedConstruction) {
            relevantApartments = mockApartments.filter(a => a.constructionId === selectedConstruction.id);
        }

        const total = relevantApartments.length;
        const sold = relevantApartments.filter(a => a.status === 'sold').length;
        const reserved = relevantApartments.filter(a => a.status === 'reserved').length;

        const soldPercentage = total > 0 ? Math.round((sold / total) * 100) : 0;
        const totalProgress = total > 0 ? Math.round(((sold + reserved) / total) * 100) : 0;

        return { total, sold, reserved, soldPercentage, totalProgress };
    }, [selectedConstruction, showAllConstructions]);

    return (
        <div className="flex flex-col md:flex-row gap-6">
            {/* Sol Taraf: Profesyonel Profil Başlığı */}
            <div className="flex-1 flex flex-col justify-center gap-1">
                <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] uppercase font-bold tracking-wider">
                        {user.role}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-border-dark/50"></span>
                    <span className="text-xs text-text-tertiary font-medium">{today}</span>
                </div>

                <h1 className="text-3xl font-bold text-text-primary tracking-tight">
                    {user.name}
                </h1>

                <div className="flex items-center gap-2 text-sm text-text-secondary mt-1">
                    <Building2 size={16} className="text-primary/70" />
                    <span className="font-medium">
                        {showAllConstructions ? 'Tüm Projeler Yönetimi' : selectedConstruction?.name}
                    </span>
                    {!showAllConstructions && selectedConstruction && (
                        <span className="text-text-tertiary text-xs">
                            — {selectedConstruction.location}
                        </span>
                    )}
                </div>
            </div>

            {/* Sağ Taraf: Proje Satış Durumu (Progress Bar) */}
            <div className="flex-1 bg-white dark:bg-bg-secondary p-5 rounded-2xl border border-border-light shadow-sm hover:border-primary/30 transition-all group">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center text-success group-hover:scale-110 transition-transform">
                            <TrendingUp size={20} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-text-primary">Satış Durumu</h3>
                            <p className="text-[11px] text-text-tertiary font-medium uppercase tracking-wide">DOLULUK & CİRO HEDEFİ</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-text-primary">%{salesStats.soldPercentage}</div>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="relative h-3 w-full bg-bg-tertiary rounded-full overflow-hidden">
                    {/* Satılan Kısım */}
                    <div
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-success to-success-dark rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${salesStats.soldPercentage}%` }}
                    />
                    {/* Rezerve Kısım (Opsiyonel - Sold'un bittiği yerden başlar görsel olarak ama basitlik için üstüne bindirmeden ayrı hesaplanabilir veya stack edilebilir. Şimdilik sadece Satılan'ı gösteriyoruz net olsun diye) */}

                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 bg-[url('/patterns/diagonal-stripes.png')] opacity-20" />
                </div>

                <div className="flex justify-between items-center mt-3 text-xs font-medium">
                    <div className="text-text-secondary">
                        <strong className="text-text-primary">{salesStats.sold}</strong> Satılan
                        <span className="mx-1 text-border-dark">/</span>
                        <span className="text-text-tertiary">{salesStats.total} Toplam Daire</span>
                    </div>
                    {salesStats.reserved > 0 && (
                        <div className="flex items-center gap-1.5 text-warning-dark bg-warning/10 px-2 py-0.5 rounded-md">
                            <span className="w-1.5 h-1.5 rounded-full bg-warning animate-pulse"></span>
                            {salesStats.reserved} Rezerve
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
